import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner, Modal, ModalHeader, ModalBody, Row } from "reactstrap";
import DataTable from "react-data-table-component";
import { Add, ViewPackageTitle, PackageTitle } from "../../../Utils/Constants";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios from "axios";
import useCurrency, { baseUrl, mediaUrl } from "../../../Utils/FileUpload";
import { Package } from "../../../Types/Tables.type";
import FilterComponent from "../../../Components/Tables/DataTables/Common/FilterComponent";
import { toast } from "react-toastify";
import { Btn } from "../../../AbstractElements";
import { useNavigate } from "react-router-dom";
import { CreditCard, Star, Clock, User, DollarSign, Info } from "react-feather";
import { useDebouncedCallback } from "use-debounce";
import { useAuth } from "../../../Context/AuthContext";

export default function ViewPackage() {
  const [filterText, setFilterText] = useState("");
  const [filteredItems, setFilteredItems] = useState<Package[]>([]);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [disabledDelete, setDisabledDelete] = useState<string | null>(null);
  const [disableAllDelete, setDisableAllDelete] = useState(false);
  const token = localStorage.getItem("token");
  const { currency } = useCurrency();
  const { hasPermission } = useAuth();

  // Pagination & Sorting State
  const [totalRows, setTotalRows] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortField, setSortField] = useState<string>("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Detail Modal State
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const [baseValues, setBaseValues] = useState({
    base_amount: 0,
    base_customer_credit_point: 0,
    base_call_duration: 0,
    base_agent_point: 0,
    base_agent_payment: 0,
  });
  const [disabledEnable, setDisabledEnable] = useState<string | null>(null);
  const [disableAllEnable, setDisableAllEnable] = useState(false);

  const fetchAllData = async (
    page = currentPage,
    size = perPage,
    search = filterText,
    field = sortField,
    direction = sortDirection
  ) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      // Fetch packages and base values in parallel
      const [packageRes, baseRes] = await Promise.all([
        axios.get(`${baseUrl}package/get_all_paginated`, {
          params: {
            page: page,
            per_page: size,
            search: search,
            sort_field: field,
            sort_direction: direction,
          },
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${baseUrl}package_payment_setting/getall`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const packages: Package[] = packageRes.data.data || [];
      setTotalRows(packageRes.data.total || 0);

      const baseData = baseRes.data[0];
      const baseValuesObj = {
        base_amount: parseFloat(baseData.base_amount),
        base_customer_credit_point: parseFloat(
          baseData.base_customer_credit_point
        ),
        base_call_duration: parseFloat(baseData.base_call_duration),
        base_agent_point: parseFloat(baseData.base_agent_point),
        base_agent_payment: parseFloat(baseData.base_agent_payment),
      };

      const updatedPackages = packages.map((pkg) => {
        const inputAmount =
          typeof pkg.amount === "string"
            ? parseFloat(pkg.amount)
            : pkg.amount;
        const baseAmount = baseValuesObj.base_amount;

        return {
          ...pkg,
          base_customer_credit_point: parseFloat(
            (
              (inputAmount / baseAmount) *
              baseValuesObj.base_customer_credit_point
            ).toFixed(2)
          ),
          base_call_duration: parseFloat(
            (
              (inputAmount / baseAmount) *
              baseValuesObj.base_call_duration
            ).toFixed(2)
          ),
          base_agent_point: parseFloat(
            (
              (inputAmount / baseAmount) *
              baseValuesObj.base_agent_point
            ).toFixed(2)
          ),
          base_agent_payment: parseFloat(
            (
              (inputAmount / baseAmount) *
              baseValuesObj.base_agent_payment
            ).toFixed(2)
          ),
        };
      });

      setBaseValues(baseValuesObj);
      setFilteredItems(updatedPackages);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Failed to load packages.");
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useDebouncedCallback((searchQuery: string) => {
    setCurrentPage(1);
    fetchAllData(1, perPage, searchQuery, sortField, sortDirection);
  }, 400);

  useEffect(() => {
    fetchAllData(currentPage, perPage, filterText, sortField, sortDirection);
  }, [refresh]);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFilterText(value);
    debouncedFetch(value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchAllData(page, perPage, filterText, sortField, sortDirection);
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
    fetchAllData(page, newPerPage, filterText, sortField, sortDirection);
  };

  const handleSort = (column: any) => {
    const field = column.sortField || "created_at";
    const newDirection: "asc" | "desc" =
      field === sortField
        ? sortDirection === "asc"
          ? "desc"
          : "asc"
        : "asc";

    setSortField(field);
    setSortDirection(newDirection);
    fetchAllData(1, perPage, filterText, field, newDirection);
  };

  const handleAdd = () => {
    navigate("/add_package");
  };

  const handleEdit = (id: string) => {
    navigate("/edit_package", { state: { id: id } });
  };

  const handleViewDetails = (pkg: Package) => {
    setSelectedPackage(pkg);
    setDetailModalOpen(true);
  };

  const handleDelete = (id: string) => {
    setDisabledDelete(id);
    setDisableAllDelete(true);
    const confirmToast = () =>
      toast.warn(
        <div>
          <div>Are you sure you want to delete this package?</div>
          <div className="d-flex justify-content-end mt-2">
            <Button
              color="danger"
              onClick={() => confirmDelete(id)}
              disabled={disabledDelete === id}
            >
              Yes
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                setDisabledDelete(null);
                setDisableAllDelete(false);
                toast.dismiss();
              }}
              className="ms-2"
            >
              Cancel
            </Button>
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: false,
          closeOnClick: false,
          closeButton: false,
          draggable: false,
          onClose: () => {
            setDisableAllDelete(false);
            setDisabledDelete(null);
          },
        }
      );

    const confirmDelete = async (id: string) => {
      setDisabledDelete(null);
      setDisableAllDelete(false);
      toast.dismiss();
      try {
        await axios.post(
          `${baseUrl}package/delete`,
          { id },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success(`Package Deleted Successfully`, {
          position: "top-right",
          autoClose: 1000,
          closeOnClick: true,
        });
        setRefresh((prev) => prev + 1);
      } catch (error) {
        console.error("Error deleting package:", error);
        toast.error("An error occurred while deleting the package", {
          position: "top-right",
          autoClose: 1000,
          closeOnClick: true,
        });
      }
    };

    confirmToast();
  };

  const toggleEnable = (id: string, currentStatus: number) => {
    setDisabledEnable(id);
    setDisableAllEnable(true);

    const confirmToggle = () => {
      toast.warn(
        <div>
          <div>
            Are you sure you want to{" "}
            <strong>{currentStatus === 1 ? "disable" : "enable"}</strong> this
            package?
          </div>
          <div className="d-flex justify-content-end mt-2">
            <Button
              color={currentStatus === 1 ? "danger" : "success"}
              onClick={() => confirmToggleEnable(id, currentStatus)}
            >
              Yes
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                setDisabledEnable(null);
                setDisableAllEnable(false);
                toast.dismiss();
              }}
              className="ms-2"
            >
              Cancel
            </Button>
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: false,
          closeOnClick: false,
          closeButton: false,
          draggable: false,
          onClose: () => {
            setDisabledEnable(null);
            setDisableAllEnable(false);
          },
        }
      );
    };

    const confirmToggleEnable = async (id: string, currentStatus: number) => {
      setDisabledEnable(null);
      setDisableAllEnable(false);
      toast.dismiss();

      try {
        const newStatus = currentStatus === 1 ? 0 : 1;

        await axios.post(
          `${baseUrl}package/toggle_active`,
          { id, is_active: newStatus },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        toast.success(
          `Package ${newStatus === 1 ? "enabled" : "disabled"} successfully!`,
          {
            position: "top-right",
            autoClose: 1000,
            closeOnClick: true,
          }
        );
        setRefresh((prev) => prev + 1);
      } catch (error) {
        console.error("Error toggling package:", error);
        toast.error("Error updating enable status", {
          position: "top-right",
          autoClose: 1000,
        });
      }
    };

    confirmToggle();
  };

  const renderHeader = (title: string, field: string) => {
    const isSorted = sortField === field;
    return (
      <div
        className="d-flex align-items-center gap-1 text-wrap"
        style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: "1.2" }}
      >
        <span>{title}</span>
        {isSorted && (
          <span style={{ color: "#16827c", fontSize: "12px", fontWeight: "bold", marginLeft: "2px" }}>
            {sortDirection === "asc" ? "▲" : "▼"}
          </span>
        )}
      </div>
    );
  };

  const packageTableColumns = [
    {
      name: <div>Sl. No.</div>,
      cell: (_row: Package, index?: number) => (
        <span>{(currentPage - 1) * perPage + (index ?? 0) + 1}</span>
      ),
      width: "7%",
    },
    {
      name: <div>Image</div>,
      cell: (row: Package) => (
        <img
          src={`${mediaUrl}/${row.image}`}
          alt={row.image}
          width={25}
          height={25}
          style={{
            objectFit: "cover",
            borderRadius: "10px",
          }}
        />
      ),
      width: "7%",
    },
    {
      name: renderHeader("Package Name", "name"),
      selector: (row: Package) => row.name,
      sortable: true,
      sortField: "name",
      width: "12%",
    },
    {
      name: renderHeader(`Recharge Amount (${currency || "₹"})`, "amount"),
      selector: (row: Package) =>
        parseFloat(String(row.amount || 0)).toFixed(2),
      width: "12%",
      sortable: true,
      sortField: "amount",
    },
    {
      name: renderHeader("Customer Points", "base_customer_credit_point"),
      selector: (row: Package) => row.base_customer_credit_point,
      sortable: true,
      sortField: "base_customer_credit_point",
      width: "12%",
    },
    {
      name: renderHeader("Call Duration (mins)", "base_call_duration"),
      selector: (row: Package) => row.base_call_duration,
      sortable: true,
      sortField: "base_call_duration",
      width: "13%",
    },
    {
      name: renderHeader("Agent Points", "base_agent_point"),
      selector: (row: Package) => row.base_agent_point,
      sortable: true,
      sortField: "base_agent_point",
      width: "10%",
    },
    {
      name: renderHeader(`Agent Payment (${currency || "₹"})`, "base_agent_payment"),
      selector: (row: Package) =>
        parseFloat(String(row.base_agent_payment || 0)).toFixed(2),
      sortable: true,
      sortField: "base_agent_payment",
      width: "11%",
    },
    {
      name: <div>Actions</div>,
      cell: (row: Package) => (
        <div className="d-flex align-items-center gap-1">
          {hasPermission("audio-package.view") && (
            <Button
              color="info"
              size="sm"
              onClick={() => handleViewDetails(row)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "4px 8px",
              }}
              title="View Details"
            >
              <i className="fa fa-eye" />
            </Button>
          )}
          {hasPermission("audio-package.enable_disable") && (
            <Button
              color={row.is_active ? "success" : "secondary"}
              size="sm"
              onClick={() => toggleEnable(row.id, row.is_active)}
              disabled={disabledEnable === row.id || disableAllEnable}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "4px 8px",
              }}
              title={row.is_active ? "Disable" : "Enable"}
            >
              <i
                className={`fa ${row.is_active ? "fa-check-circle" : "fa-ban"}`}
              />
            </Button>
          )}
          {hasPermission("audio-package.edit") && (
            <Button
              color="warning"
              size="sm"
              onClick={() => handleEdit(row.id)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "4px 8px",
              }}
              title="Edit"
            >
              <i className="fa fa-edit" />
            </Button>
          )}
          {hasPermission("audio-package.delete") && (
            <Button
              color="danger"
              size="sm"
              onClick={() => handleDelete(row.id)}
              disabled={disabledDelete === row.id || disableAllDelete}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "4px 8px",
              }}
              title="Delete"
            >
              <i className="fa fa-trash" />
            </Button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      minWidth: "160px",
      width: "16%",
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#16827c",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #16827c",
        "& span[class*='SortIcon']": {
          display: "none !important",
        },
        "& .rdt_TableCol_SortIcon": {
          display: "none !important",
        },
        "& svg": {
          display: "none !important",
        },
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid #dee2e6",
      },
    },
    table: {
      style: {
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      },
    },
  };

  return (
    <>
      <Breadcrumbs
        pageTitle={ViewPackageTitle}
        parent={PackageTitle}
        title={ViewPackageTitle}
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody>
            {hasPermission("audio-package.add") && (
              <Col xs={12} className="mt-2 d-flex justify-content-end mb-3">
                <Btn color="primary" onClick={handleAdd}>
                  {Add}
                </Btn>
              </Col>
            )}
            {hasPermission("audio-package.view") && (
              <>
                <div className="mb-3">
                  <FilterComponent
                    onFilter={handleSearchInputChange}
                    filterText={filterText}
                  />
                </div>
                <div className="table-responsive">
                  {loading ? (
                    <div className="text-center p-5">
                      <Spinner color="primary" />
                    </div>
                  ) : (
                    <DataTable
                      data={filteredItems}
                      columns={packageTableColumns}
                      pagination
                      paginationServer
                      paginationPerPage={perPage}
                      paginationTotalRows={totalRows}
                      onChangePage={handlePageChange}
                      onChangeRowsPerPage={handlePerRowsChange}
                      paginationDefaultPage={currentPage}
                      sortServer
                      onSort={handleSort}
                      sortIcon={<span style={{ display: "none" }} />}
                      highlightOnHover
                      striped
                      customStyles={customStyles}
                      responsive
                    />
                  )}
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </Col>

      {/* Package Detail View Modal */}
      <Modal isOpen={detailModalOpen} toggle={() => setDetailModalOpen(false)} size="lg" centered>
        <ModalHeader toggle={() => setDetailModalOpen(false)} className="border-bottom-0 pb-0">
          <span className="fw-bold text-dark fs-5">Audio Package Details</span>
        </ModalHeader>
        <ModalBody className="px-4 pb-4">
          {selectedPackage && (
            <div>
              {/* Hero Banner Card */}
              <div
                className="text-center mb-4 p-4 rounded-4 position-relative overflow-hidden"
                style={{
                  background: "linear-gradient(135deg, #1b9aaa 0%, #16827c 100%)",
                  color: "#ffffff",
                  boxShadow: "0 8px 24px rgba(22, 130, 124, 0.25)",
                }}
              >
                <div className="d-inline-block position-relative mb-2">
                  <img
                    src={`${mediaUrl}/${selectedPackage.image}`}
                    alt={selectedPackage.name}
                    width="80"
                    height="80"
                    style={{
                      objectFit: "cover",
                      borderRadius: "20px",
                      border: "3px solid #ffffff",
                      boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                    }}
                  />
                </div>
                <h3 className="fw-bold text-white mb-1">{selectedPackage.name}</h3>
                <div className="d-flex align-items-center justify-content-center gap-2 mt-2">
                  <span
                    className="badge rounded-pill px-3 py-2 fw-semibold"
                    style={{
                      backgroundColor: selectedPackage.is_active === 1 ? "rgba(255,255,255,0.25)" : "rgba(239, 68, 68, 0.8)",
                      backdropFilter: "blur(4px)",
                      color: "#ffffff",
                      fontSize: "12px",
                    }}
                  >
                    {selectedPackage.is_active === 1 ? "● Enabled" : "● Disabled"}
                  </span>
                  {selectedPackage.country?.name && (
                    <span
                      className="badge rounded-pill px-3 py-2 fw-semibold"
                      style={{
                        backgroundColor: "#0284c7",
                        color: "#ffffff",
                        fontSize: "12px",
                      }}
                    >
                      🌍 {selectedPackage.country.name}
                    </span>
                  )}
                  {selectedPackage.is_free === 1 && (
                    <span
                      className="badge rounded-pill px-3 py-2 fw-semibold"
                      style={{
                        backgroundColor: "rgba(245, 158, 11, 0.9)",
                        color: "#ffffff",
                        fontSize: "12px",
                      }}
                    >
                      🎁 Free Plan
                    </span>
                  )}
                </div>
              </div>

              {/* Package Description Callout */}
              {selectedPackage.description && (
                <div
                  className="p-3 mb-4 rounded-3 border-start-0 border-top-0 border-bottom-0 shadow-sm"
                  style={{
                    backgroundColor: "#f0fdfa",
                    borderLeft: "4px solid #16827c",
                  }}
                >
                  <div className="d-flex align-items-center mb-1 text-info fw-semibold small">
                    <Info size={16} className="me-1" /> Package Description
                  </div>
                  <p className="mb-0 text-dark small leading-relaxed">{selectedPackage.description}</p>
                </div>
              )}

              {/* Metrics Grid */}
              <Row className="g-3">
                <Col md={6}>
                  <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center me-3"
                      style={{ width: "44px", height: "44px", backgroundColor: "#e6f8f6", color: "#16827c" }}
                    >
                      <CreditCard size={20} />
                    </div>
                    <div>
                      <span className="text-muted small d-block fw-semibold">Recharge Amount</span>
                      <strong className="text-dark fs-5">{selectedPackage.amount} {currency}</strong>
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center me-3"
                      style={{ width: "44px", height: "44px", backgroundColor: "#f3e8ff", color: "#7367f0" }}
                    >
                      <Star size={20} />
                    </div>
                    <div>
                      <span className="text-muted small d-block fw-semibold">Customer Credit Points</span>
                      <strong className="text-dark fs-5">{selectedPackage.base_customer_credit_point}</strong>
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center me-3"
                      style={{ width: "44px", height: "44px", backgroundColor: "#e0f2fe", color: "#0284c7" }}
                    >
                      <Clock size={20} />
                    </div>
                    <div>
                      <span className="text-muted small d-block fw-semibold">Call Duration</span>
                      <strong className="text-dark fs-5">{selectedPackage.base_call_duration} Mins</strong>
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center me-3"
                      style={{ width: "44px", height: "44px", backgroundColor: "#fff7ed", color: "#ea580c" }}
                    >
                      <User size={20} />
                    </div>
                    <div>
                      <span className="text-muted small d-block fw-semibold">Agent Points</span>
                      <strong className="text-dark fs-5">{selectedPackage.base_agent_point}</strong>
                    </div>
                  </div>
                </Col>

                <Col md={12}>
                  <div className="p-3 border rounded-3 bg-white shadow-sm d-flex align-items-center">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center me-3"
                      style={{ width: "44px", height: "44px", backgroundColor: "#ecfdf5", color: "#10b981" }}
                    >
                      <DollarSign size={20} />
                    </div>
                    <div>
                      <span className="text-muted small d-block fw-semibold">Agent Payment</span>
                      <strong className="text-success fs-5">{selectedPackage.base_agent_payment} {currency}</strong>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
}
